import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * useWebSocket hook manages the WebSocket connection to the coaching server.
 * Handles automatic reconnection with exponential backoff.
 *
 * @param {string} url - WebSocket server URL
 * @returns {{ status, transcripts, suggestion, streamingTokens, sendMessage }}
 */
const URGENCY_TIMEOUT = { critical: 0, warning: 30000, info: 20000, positive: 15000 };

export default function useWebSocket(url) {
  const [status, setStatus] = useState('disconnected');
  const [transcripts, setTranscripts] = useState([]);
  const [suggestion, setSuggestion] = useState(null);
  const [lastSuggestion, setLastSuggestion] = useState(null);
  const [suggestionHistory, setSuggestionHistory] = useState([]);
  const [streamingTokens, setStreamingTokens] = useState('');
  const [reports, setReports] = useState([]);
  const [latestReportId, setLatestReportId] = useState(null);
  const [customerIntel, setCustomerIntel] = useState([]);
  const [announcement, setAnnouncement] = useState(null);
  const [voicemails, setVoicemails] = useState([]);
  const [callBlocked, setCallBlocked] = useState(null);
  const [researchItems, setResearchItems] = useState([]);
  const [researchLoading, setResearchLoading] = useState(false);
  const [crmContact, setCrmContact] = useState(null);
  const [roleLabels, setRoleLabels] = useState({ agent: 'Agent', customer: 'Customer' });
  const [hideTranscript, setHideTranscript] = useState(false);
  const [speakerMap, setSpeakerMap] = useState({});
  const [billingAlerts, setBillingAlerts] = useState([]);
  const wsRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const suggestionTimerRef = useRef(null);
  const currentSuggestionRef = useRef(null);

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      setStatus('connected');
      reconnectAttemptRef.current = 0;
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        handleMessage(msg);
      } catch (err) {
        console.error('Failed to parse server message:', err);
      }
    };

    ws.onclose = () => {
      setStatus('disconnected');
      scheduleReconnect();
    };

    ws.onerror = (err) => {
      console.error('WebSocket error:', err);
      setStatus('error');
    };
  }, [url]);

  function handleMessage(msg) {
    switch (msg.type) {
      case 'transcript':
        if (!msg.data.isPartial) {
          setTranscripts((prev) => [...prev.slice(-49), msg.data]);
        }
        break;

      case 'speaker.map':
        setSpeakerMap(msg.data.speakerMap || {});
        break;

      case 'suggestion': {
        setStreamingTokens('');
        const incoming = { ...msg.data, arrivedAt: Date.now() };
        const previous = currentSuggestionRef.current;
        if (previous) {
          setSuggestionHistory((prev) => [previous, ...prev].slice(0, 5));
        }
        currentSuggestionRef.current = incoming;
        setSuggestion(incoming);
        setLastSuggestion(null);
        if (suggestionTimerRef.current) clearTimeout(suggestionTimerRef.current);
        const timeout = URGENCY_TIMEOUT[incoming.urgency] ?? URGENCY_TIMEOUT.info;
        if (timeout > 0) {
          suggestionTimerRef.current = setTimeout(() => {
            setLastSuggestion(currentSuggestionRef.current);
            setSuggestion(null);
            currentSuggestionRef.current = null;
          }, timeout);
        }
        break;
      }

      case 'token':
        setStreamingTokens((prev) => prev + msg.data.token);
        break;

      case 'customer.intel':
      case 'customer.intel.replace':
        setCustomerIntel(msg.data.items || []);
        break;

      case 'research.update': {
        const items = msg.data.items || [];
        setResearchItems(items);
        setResearchLoading(false);
        break;
      }

      case 'research.searching':
        setResearchLoading(true);
        break;

      case 'crm.context':
        setCrmContact(msg.data);
        break;

      case 'call.started':
        setTranscripts([]);
        setSuggestion(null);
        setLastSuggestion(null);
        setSuggestionHistory([]);
        currentSuggestionRef.current = null;
        setStreamingTokens('');
        setCustomerIntel([]);
        setResearchItems([]);
        setResearchLoading(false);
        setCallBlocked(null);
        setSpeakerMap({});
        setCrmContact(msg.data.callerNumber ? { phone: msg.data.callerNumber } : null);
        if (msg.data.roleLabels) setRoleLabels(msg.data.roleLabels);
        else setRoleLabels({ agent: 'Agent', customer: 'Customer' });
        setHideTranscript(!!msg.data.hideTranscript);
        setStatus('in-call');
        break;

      case 'call.blocked':
        setCallBlocked(msg.data);
        break;

      case 'call.ended':
        setCrmContact(null);
        setStatus('connected');
        break;

      case 'call.report':
        setTranscripts([]);
        setSuggestion(null);
        setLastSuggestion(null);
        setSuggestionHistory([]);
        currentSuggestionRef.current = null;
        setStreamingTokens('');
        setCustomerIntel([]);
        setResearchItems([]);
        setResearchLoading(false);
        setCrmContact(null);
        setHideTranscript(false);
        setReports((prev) => [msg.data, ...prev].slice(0, 50));
        setLatestReportId(msg.data.callSid);
        break;

      case 'announcement':
        setAnnouncement(msg.data);
        break;

      case 'voicemail.new':
        setVoicemails((prev) => [msg.data, ...prev].slice(0, 50));
        break;

      case 'balance.low':
        setBillingAlerts((prev) => {
          const filtered = prev.filter((a) => a.type !== 'balance.low');
          return [...filtered, { type: 'balance.low', data: msg.data, ts: Date.now() }];
        });
        break;

      case 'trial.expiring':
        setBillingAlerts((prev) => {
          const filtered = prev.filter((a) => a.type !== 'trial.expiring');
          return [...filtered, { type: 'trial.expiring', data: msg.data, ts: Date.now() }];
        });
        break;

      case 'trial.expired':
        setBillingAlerts((prev) => {
          const filtered = prev.filter((a) => a.type !== 'trial.expired' && a.type !== 'trial.expiring');
          return [...filtered, { type: 'trial.expired', data: msg.data, ts: Date.now() }];
        });
        break;

      case 'payment.failed':
        setBillingAlerts((prev) => {
          const filtered = prev.filter((a) => a.type !== 'payment.failed');
          return [...filtered, { type: 'payment.failed', data: msg.data, ts: Date.now() }];
        });
        break;

      case 'status':
        if (msg.data.status === 'connected') {
          setStatus('connected');
        }
        break;

      default:
        break;
    }
  }

  function scheduleReconnect() {
    const delay = Math.min(1000 * 2 ** reconnectAttemptRef.current, 30000);
    reconnectAttemptRef.current++;
    reconnectTimerRef.current = setTimeout(connect, delay);
  }

  const sendMessage = useCallback((msg) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg));
    }
  }, []);

  const joinCall = useCallback(
    (callSid, agentId) => {
      sendMessage({ type: 'join', data: { callSid, agentId } });
    },
    [sendMessage]
  );

  const sendSuggestionAction = useCallback(
    (id, action) => {
      sendMessage({ type: 'suggestion.action', data: { id, action } });
      if (action === 'dismissed' || action === 'accepted') {
        const captured = currentSuggestionRef.current;
        currentSuggestionRef.current = null;
        if (captured) {
          setSuggestionHistory((prev) => [captured, ...prev].slice(0, 5));
          setLastSuggestion(captured);
        }
        setSuggestion(null);
        if (suggestionTimerRef.current) clearTimeout(suggestionTimerRef.current);
      }
    },
    [sendMessage]
  );

  useEffect(() => {
    connect();
    return () => {
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (suggestionTimerRef.current) clearTimeout(suggestionTimerRef.current);
      wsRef.current?.close();
    };
  }, [connect]);

  const renameSpeaker = useCallback((speakerIndex, label) => {
    sendMessage({ type: 'speaker.rename', data: { speakerIndex, label } });
  }, [sendMessage]);

  const dismissAnnouncement = useCallback(() => setAnnouncement(null), []);
  const dismissBillingAlert = useCallback((type) => {
    setBillingAlerts((prev) => prev.filter((a) => a.type !== type));
  }, []);

  return {
    status,
    transcripts,
    suggestion,
    lastSuggestion,
    suggestionHistory,
    streamingTokens,
    reports,
    latestReportId,
    setLatestReportId,
    customerIntel,
    announcement,
    dismissAnnouncement,
    billingAlerts,
    dismissBillingAlert,
    voicemails,
    callBlocked,
    researchItems,
    researchLoading,
    crmContact,
    roleLabels,
    hideTranscript,
    speakerMap,
    renameSpeaker,
    sendMessage,
    joinCall,
    sendSuggestionAction,
  };
}
